import React, { Dispatch, SetStateAction, useMemo } from 'react'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { EvidenceToControlMappingInfo } from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_pb'
import { SelectControlsRowItem } from 'src/components/Reusable/Forms/MultiSelectFromComponents/SelectControlsRowItem'
import { NoRecommendedTemplates } from 'src/components/ModalForms/Frameworks/AddFramework/NoRecommendedTemplates'
import { Grid } from 'src/components/Reusable/Grid'
import { RefreshButton } from 'src/components/Reusable/Buttons'
import { Spinner } from 'src/Throbber'
import { FlexAlign, FlexColumn } from 'src/components/Reusable/Flex'
import { SelectedCountSpan } from 'src/components/ModalForms/ModalForm.styles'
import { getPluralization } from 'src/Utils/globalHelpers'
import {
  useEvidenceControlMappings,
  useWaitForEvidenceIndexing,
} from '../evidence.hooks'

const GRID_TEMPLATE_COLUMNS =
  'min-content minmax(min-content, auto) minmax(min-content, auto) minmax(min-content, 1fr)'

type SuggestedControlsTabProps = {
  evidenceIds: string[]
  selectedControls: string[]
  setSelectedControls: Dispatch<SetStateAction<string[]>>
  isSubmitting: boolean
  existingControlIds?: string[]
  canLink?: (control: Control) => boolean
}

export const SuggestedControlsTabLoading = (): JSX.Element => {
  return (
    <FlexColumn justify={FlexAlign.CENTER} $padding={32} gap={12}>
      <Spinner color="primary" size="xl" />
      <span>Analyzing your evidence to find suggested controls&hellip;</span>
    </FlexColumn>
  )
}

const SuggestedControlsTabRetry = ({
  message,
  onRefresh,
}: {
  message: string
  onRefresh: () => void
}): JSX.Element => {
  return (
    <FlexColumn justify={FlexAlign.CENTER} $padding={32} gap={12}>
      <span>{message}</span>
      <RefreshButton onClick={onRefresh} isRefreshing={false} />
    </FlexColumn>
  )
}

const SuggestedControlsTabBody = ({
  mappings,
  isLoading,
  selectedControls,
  setSelectedControls,
  isSubmitting,
  existingControlIds,
  canLink,
}: {
  mappings: EvidenceToControlMappingInfo[]
  isLoading: boolean
  selectedControls: string[]
  setSelectedControls: Dispatch<SetStateAction<string[]>>
  isSubmitting: boolean
  existingControlIds?: string[]
  canLink?: (control: Control) => boolean
}): JSX.Element => {
  const suggestedControls = useMemo((): Control[] => {
    const seen = new Set<string>()
    const result: Control[] = []
    for (const mapping of mappings) {
      for (const control of mapping.getSuggestedControlsList()) {
        const id = control.getId()
        if (
          !seen.has(id) &&
          !existingControlIds?.includes(id) &&
          (!canLink || canLink(control))
        ) {
          seen.add(id)
          result.push(control)
        }
      }
    }
    return result
  }, [mappings, existingControlIds, canLink])

  if (isLoading) {
    return <SuggestedControlsTabLoading />
  }

  if (suggestedControls.length === 0) {
    return <NoRecommendedTemplates templateType="suggested controls" />
  }

  return (
    <>
      <SelectedCountSpan>
        {`${selectedControls.length} ${getPluralization(
          'control',
          selectedControls.length,
        )} selected`}
      </SelectedCountSpan>
      <div
        style={{
          maxHeight: '350px',
          overflowY: 'auto',
          width: '100%',
          marginTop: '25px',
        }}
      >
        <Grid gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
          {suggestedControls.map((control) => (
            <SelectControlsRowItem
              key={control.getId()}
              control={control}
              setSelectedControlIds={setSelectedControls}
              selectedControlIds={selectedControls}
              disabled={isSubmitting}
            />
          ))}
        </Grid>
      </div>
    </>
  )
}

export const SuggestedControlsTab = (
  props: SuggestedControlsTabProps,
): JSX.Element => {
  const idSet = new Set(props.evidenceIds)
  const { isIndexed, isTimedOut, restart } = useWaitForEvidenceIndexing(
    props.evidenceIds,
  )
  const { mappings, isLoading, isRefreshing, hasError, refresh } =
    useEvidenceControlMappings({
      evidenceIds: props.evidenceIds,
      enabled: isIndexed,
    })

  if (isTimedOut && !isIndexed) {
    return (
      <SuggestedControlsTabRetry
        message="Still analyzing your evidence. This can take a few minutes."
        onRefresh={restart}
      />
    )
  }
  if (hasError && !isRefreshing) {
    return (
      <SuggestedControlsTabRetry
        message="Couldn't load suggested controls."
        onRefresh={refresh}
      />
    )
  }

  const filteredMappings = mappings.filter((m) => {
    const e = m.getEvidence()
    if (!e) return false
    const rowId = e.getEvidenceId() || e.getId() || ''
    return idSet.has(rowId)
  })
  return (
    <SuggestedControlsTabBody
      mappings={filteredMappings}
      isLoading={!isIndexed || isLoading || isRefreshing}
      selectedControls={props.selectedControls}
      setSelectedControls={props.setSelectedControls}
      isSubmitting={props.isSubmitting}
      existingControlIds={props.existingControlIds}
      canLink={props.canLink}
    />
  )
}
